import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChatKit, useChatKit } from '@openai/chatkit-react'
import { composeChatKitOptions, type ChatKitOptionsOverrides } from '../chatkit/options'

const USER_ID_STORAGE_KEY = 'chatkit:test_user_id'
const DEFAULT_SITE_CODE = (import.meta.env.VITE_CHATKIT_SITE_CODE ?? 'adm').toLowerCase()
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

interface ChatKitModeResponse {
  mode: 'imported' | 'sessions'
  workflow_id: string | null
}

interface SessionResponse {
  client_secret: string
  session_id: string
  expires_at: number
  workflow_id: string
}

function getOrCreateUserId(): string {
  if (typeof window === 'undefined') {
    return 'anonymous-user'
  }

  const existing = window.localStorage.getItem(USER_ID_STORAGE_KEY)
  if (existing) {
    return existing
  }

  const generated =
    window.crypto?.randomUUID?.() ?? `user_${Math.random().toString(36).slice(2)}`
  window.localStorage.setItem(USER_ID_STORAGE_KEY, generated)
  return generated
}

function createChatKitFetch(userId: string, siteCode: string) {
  return async (...args: Parameters<typeof fetch>) => {
    const [input, init] = args

    // Handle file uploads (FormData)
    if (init?.body instanceof FormData) {
      const formData = init.body
      formData.append('user_id', userId)
      formData.append('site_code', siteCode)
      return fetch(input, init)
    }

    // Handle ChatKit JSON requests
    if (init?.body && typeof init.body === 'string') {
      try {
        const parsed = JSON.parse(init.body) as Record<string, unknown>
        const metadata =
          typeof parsed.metadata === 'object' && parsed.metadata !== null
            ? { ...(parsed.metadata as Record<string, unknown>) }
            : {}
        metadata.user_id = userId
        metadata.site_code = siteCode

        const nextInit = {
          ...init,
          body: JSON.stringify({ ...parsed, metadata }),
        }

        return fetch(input, nextInit)
      } catch (error) {
        console.warn('Failed to enrich ChatKit payload metadata', error)
      }
    }

    return fetch(input, init)
  }
}

export default function ChatTest() {
  const { t } = useTranslation()
  const userId = useMemo(() => getOrCreateUserId(), [])
  const siteCode = DEFAULT_SITE_CODE
  const chatkitFetch = useMemo(() => createChatKitFetch(userId, siteCode), [userId, siteCode])

  // Sessions mode state
  const [mode, setMode] = useState<'imported' | 'sessions' | 'loading'>('loading')
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [sessionError, setSessionError] = useState<string | null>(null)

  // Check mode and get session if needed
  useEffect(() => {
    async function initMode() {
      try {
        const modeRes = await fetch(`${API_BASE_URL}/chatkit/mode`)
        const modeData: ChatKitModeResponse = await modeRes.json()

        if (modeData.mode === 'sessions') {
          // Get client_secret for sessions mode
          const sessionRes = await fetch(`${API_BASE_URL}/chatkit/session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: userId }),
          })

          if (!sessionRes.ok) {
            const error = await sessionRes.json()
            throw new Error(error.detail || 'Failed to create session')
          }

          const sessionData: SessionResponse = await sessionRes.json()
          setClientSecret(sessionData.client_secret)
          setMode('sessions')
        } else {
          setMode('imported')
        }
      } catch (error) {
        console.error('Failed to initialize ChatKit mode:', error)
        setSessionError(error instanceof Error ? error.message : 'Unknown error')
        setMode('imported') // Fallback to imported mode
      }
    }

    initMode()
  }, [userId])

  const chatkitOptions = useMemo(() => {
    const runtimeOverrides =
      typeof window !== 'undefined'
        ? ((window as unknown as { __CHATKIT_OPTIONS__?: ChatKitOptionsOverrides })
          .__CHATKIT_OPTIONS__ ?? undefined)
        : undefined

    // Sessions mode: use clientSecret instead of domainKey
    // ChatKit SDK accepts clientSecret for session-based auth (not in types yet)
    if (mode === 'sessions' && clientSecret) {
      return composeChatKitOptions(runtimeOverrides, {
        api: {
          clientSecret: clientSecret,
        },
      } as ChatKitOptionsOverrides)
    }

    // Imported mode: use backend proxy
    return composeChatKitOptions(runtimeOverrides, {
      api: {
        url: '/api/chatkit',
        domainKey: import.meta.env.VITE_OPENAI_DOMAIN_KEY,
        fetch: chatkitFetch,
        uploadStrategy: {
          type: 'direct',
          uploadUrl: `${API_BASE_URL}/files/upload`,
        },
      },
    })
  }, [chatkitFetch, mode, clientSecret])

  const chatkit = useChatKit(chatkitOptions)

  // Loading state
  if (mode === 'loading') {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-white">{t('chatTest.title')}</h1>
          <p className="text-muted-200 mt-1">{t('chatTest.subtitle')}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-surface-200/80 backdrop-blur p-6">
          <div className="flex items-center justify-center h-[600px]">
            <div className="text-muted-200">Loading ChatKit...</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold text-white">{t('chatTest.title')}</h1>
        <p className="text-muted-200 mt-1">
          {t('chatTest.subtitle')}
          <span className="ml-2 text-xs text-slate-500">
            (mode: {mode}{sessionError ? `, error: ${sessionError}` : ''})
          </span>
        </p>
      </div>

      <div className="rounded-xl border border-slate-800 bg-surface-200/80 backdrop-blur p-6">
        <div style={{ height: '600px' }}>
          <ChatKit control={chatkit.control} />
        </div>
      </div>
    </div>
  )
}
